import { ArrowLeftIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { Modal } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { BlogPostStatusEnum } from '../../../@types/BlogPost';
import type { Company } from '../../../@types/Company';
import type { RootState } from '../../../store/store';
import { JWTHeader } from '../../../utils/JWTHeader';
import { buildSuggestedMailText } from '../../../utils/newsletterMail';
import { exportNewsletterToPDF } from '../../../utils/newsletterPDF';
import { processFixedNewsletterContent } from '../../../utils/newsletterPreview';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge/Badge.module';
import { showToast } from '../../common/Notification';
import CompanyBrandingEdition from '../../Communication/components/CompanyBrandingEdition/CompanyBrandingEdition.module';

interface NewsletterForPDF {
  id: string;
  subject: string;
  content: string;
  status: string;
  type: string;
  createdAt: string;
}

interface FixedNewsletterData {
  date: string;
  slug: string;
  templateName: string;
  title: string;
  content: string;
  pageCount: number;
}

function NewsletterPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company) as Company;

  const [newsletterData, setNewsletterData] = useState<FixedNewsletterData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processedContent, setProcessedContent] = useState<string>('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [showBrandingEditor, setShowBrandingEditor] = useState(false);

  useEffect(() => {
    if (!id || !jwt) {
      navigate(ROUTES.ADMIN_NEWSLETTER);
      return;
    }

    const loadFixedNewsletter = async() => {
      try {
        // Parse the fixed newsletter ID: "fixed:date:slug:templateName"
        const parts = id.split(':');
        if (parts.length !== 4 || parts[0] !== 'fixed') {
          throw new Error('Invalid fixed newsletter ID format');
        }

        const [, date, slug, templateName] = parts;

        // Fetch the newsletter data
        const response = await fetch(
          `/api/fixed-newsletters/${date}/${slug}`,
          JWTHeader(jwt),
        );

        if (!response.ok) {
          throw new Error('Failed to fetch newsletter');
        }

        const data = await response.json();

        // Find the specific template
        const template = data.templates.find((t: { name: string; content: string; pageCount?: number }) => t.name === templateName);
        if (!template) {
          throw new Error('Template not found');
        }

        setNewsletterData({
          date,
          slug,
          templateName,
          title: data.title,
          content: template.content,
          pageCount: template.pageCount || 0,
        });
      } catch (error) {
        console.error('Error loading fixed newsletter:', error);
        showToast('error', 'Erreur lors du chargement de la newsletter');
        navigate(ROUTES.ADMIN_NEWSLETTER);
      } finally {
        setIsLoading(false);
      }
    };

    loadFixedNewsletter();
  }, [id, jwt, navigate]);

  // Process the newsletter content with company branding
  useEffect(() => {
    const processContent = async() => {
      if (!newsletterData || !company) {return;}

      try {
        const processed = await processFixedNewsletterContent(
          newsletterData.content,
          company,
          jwt, // Pass JWT here
        );
        setProcessedContent(processed);
      } catch (error) {
        console.error('Error processing newsletter content:', error);
        showToast('error', 'Une erreur est survenue lors du traitement du contenu de la newsletter.');
      }
    };

    processContent();
  }, [
    newsletterData,
    jwt, // Add JWT dependency
    // Only depend on the specific company branding properties that affect newsletter processing
    company?.id,
    company?.brandingSettings?.color1,
    company?.brandingSettings?.color2,
    company?.brandingSettings?.color3,
    company?.brandingSettings?.referents,
    company?.brandingSettings?.brandingImageName,
  ]);

  // Update resize observer effect for responsive scaling
  useEffect(() => {
    const newsletterContainer = document.querySelector('#newsletter-content') as HTMLElement;
    
    if (!newsletterContainer) {
      console.warn('Newsletter scaling: Container #newsletter-content not found');
      return;
    }

    // Find the actual container that changes size with window resize
    // This should be the flex container that wraps the newsletter content
    const flexContainer = newsletterContainer.closest('.flex') as HTMLElement;
    const observableContainer = flexContainer || newsletterContainer.parentElement as HTMLElement;

    // The content element is the one with the transform: scale style applied
    const content = newsletterContainer.querySelector('div[style*="width: 210mm"]') as HTMLElement | null ||
                   newsletterContainer.querySelector('div[style*="transform: scale"]') as HTMLElement | null;
    
    if (!content) {
      console.warn('Newsletter scaling: No suitable content element found');
      return;
    }
    
    const updateScale = () => {
      // Get the actual available width - use the observable container's width minus padding
      const containerWidth = observableContainer ? observableContainer.clientWidth - 32 : newsletterContainer.clientWidth; // Account for padding
      // 210mm = 793.7007874016 pixels at 96 DPI (more precise calculation)
      const contentWidth = (210 / 25.4) * 96; // mm to inches to pixels
      const scaleFactor = Math.min(1, containerWidth / contentWidth);
      
      
      // Apply the scale factor
      content.style.setProperty('--scale-factor', scaleFactor.toString());
    };
    
    const resizeObserver = new ResizeObserver(() => {
      updateScale();
    });

    // Observe the container that actually changes size with window resize
    if (observableContainer) {
      resizeObserver.observe(observableContainer);
    }
    
    // Also listen to window resize as a fallback
    const handleWindowResize = () => {
      setTimeout(updateScale, 10); // Small delay to let layout settle
    };
    
    window.addEventListener('resize', handleWindowResize);
    
    // Trigger initial calculation
    updateScale();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [processedContent]);

  // Helper function to manage scale factor for PDF export
  const setContentScale = (scale: number, compensateWrapper: boolean = false) => {
    // Use the same selector logic as the ResizeObserver
    const container = document.querySelector('#newsletter-content') as HTMLElement;
    const content = container?.querySelector('div[style*="width: 210mm"]') as HTMLElement | null ||
                   container?.querySelector('div[style*="transform: scale"]') as HTMLElement | null;
    
    if (content) {
      content.style.setProperty('--scale-factor', scale.toString());
      if (compensateWrapper) {
        // Store original styles before modifying
        const originalStyles = {
          width: content.style.width,
          transform: content.style.transform,
          transformOrigin: content.style.transformOrigin,
          margin: content.style.margin,
        };
        
        // Apply PDF export styles
        content.style.width = `${210 * 3.78 * (1 / scale)}px`;
        content.style.transform = 'none';
        content.style.transformOrigin = 'initial';
        content.style.margin = '0';
        
        return originalStyles;
      }
      
      // Reset to default styles for A4
      content.style.width = '210mm';
      content.style.transform = `scale(var(--scale-factor, 1))`;
      content.style.transformOrigin = 'top left';
      content.style.margin = '0 auto';
    }
    return null;
  };

  const handleDownloadPDF = async() => {
    try {
      if (!newsletterData || !company) {
        throw new Error('Missing required data');
      }

      // Store current scale factor and styles
      const container = document.querySelector('#newsletter-content') as HTMLElement;
      const content = container?.querySelector('div[style*="width: 210mm"]') as HTMLElement | null ||
                     container?.querySelector('div[style*="transform: scale"]') as HTMLElement | null;
      const currentScale = content?.style.getPropertyValue('--scale-factor') || '1';
      
      // Reset scale to 1 for PDF export and compensate wrapper, store original styles
      const originalStyles = setContentScale(1, true);

      // Create a newsletter object compatible with PDF export
      const newsletterForPDF: NewsletterForPDF = {
        id: `fixed:${newsletterData.date}:${newsletterData.slug}:${newsletterData.templateName}`,
        subject: newsletterData.title,
        content: processedContent,
        status: BlogPostStatusEnum.PUBLIC,
        type: 'fixed',
        createdAt: new Date().toISOString(),
      };

      await exportNewsletterToPDF(newsletterForPDF);
      showToast('success', 'Newsletter exportée en PDF avec succès.');

      // Restore previous scale and original styles
      if (content && originalStyles) {
        Object.assign(content.style, originalStyles);
        content.style.setProperty('--scale-factor', currentScale);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showToast('error', 'Une erreur est survenue lors de l\'export de la newsletter en PDF. Veuillez vérifier que tous les liens sont valides.');
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="animate-pulse">
          <div className="mb-4 h-8 w-1/3 rounded bg-gray-200"></div>
          <div className="mb-6 h-4 w-1/2 rounded bg-gray-200"></div>
          <div className="h-96 rounded bg-gray-200"></div>
        </div>
      </div>
    );
  }

  if (!newsletterData) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-md">
        <p className="text-red-600">Newsletter non trouvée</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 rounded-lg bg-white p-4 shadow-md">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(ROUTES.ADMIN_NEWSLETTER)}
              className="mr-4 flex items-center text-rgpd-blue"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <h1 className="grow text-2xl font-bold">Prévisualisation de la newsletter</h1>
          </div>

          {/* Suggested Mail Content Section */}
          <div className="border-t pt-6">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-700">Contenu du mail de Newsletter suggéré et RGPD compatible</h2>
              <div className="flex gap-2">
                <button
                  onClick={async() => {
                    try {
                      const text = buildSuggestedMailText(company, newsletterData.title, newsletterData.date);
                      await navigator.clipboard.writeText(text);
                      showToast('success', 'Contenu du mail copié dans le presse-papiers.');
                    } catch {
                      showToast('error', 'Impossible de copier le contenu du mail.');
                    }
                  }}
                  className="rounded-lg bg-rgpd-blue px-4 py-2 text-white transition-colors hover:bg-rgpd-blue-dark"
                >
                  Copier le contenu
                </button>
              </div>
            </div>
            <div className="mb-6">
              <textarea
                className="w-full rounded-lg border p-3 text-sm text-gray-800"
                rows={8}
                readOnly
                value={newsletterData ? buildSuggestedMailText(company, newsletterData.title, newsletterData.date) : ''}
              />
            </div>
          </div>

          {/* Company Branding Edition Section */}
          <div className="border-t pt-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-700">Personnalisation en temps réel</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowBrandingEditor(!showBrandingEditor)}
                  className="rounded-lg bg-rgpd-blue px-4 py-2 text-white transition-colors hover:bg-rgpd-blue-dark"
                >
                  {showBrandingEditor ? 'Masquer l\'édition de l\'image de l\'entreprise' : 'Modifier l\'image de l\'entreprise'}
                </button>
              </div>
            </div>
            
            {showBrandingEditor && (
              <div className="mb-6">
                <CompanyBrandingEdition showBlogStructure={false} />
              </div>
            )}
          </div>
          
          {/* Legal Warning Badge for fixed newsletters */}
          <Badge
            mode="warning"
            type="solid"
            size="medium"
            textAlign="left"
            className="mt-4 mb-4 w-full"
            text={
              <div className="text-sm">
                <span className="text-lg font-semibold">Newsletter Fixe (Admin) :</span>
                <div className="mx-4 my-2">
                  <p>
                    Cette newsletter est un modèle prédéfini. Le contenu ne peut pas être modifié.<br/>
                    Template: {newsletterData.templateName} | Date: {newsletterData.date}
                  </p>
                </div>
              </div>
            }
          />

          <div className="rounded-lg border border-gray-200 bg-gray-50">
            <div className="mb-4 rounded-lg bg-white p-4 shadow-md">
              <div className="items-center justify-between gap-4 md:flex">
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-gray-800">
                    Objet : {newsletterData.title}
                  </h1>
                </div>
                <div className="hidden h-12 w-px bg-gray-200 md:block" />
                <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
                  <button 
                    className="flex w-full items-center justify-center rounded-xl border border-rgpd-blue px-4 py-2 text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-rgpd-white md:w-auto"
                    onClick={handleDownloadPDF}
                  >
                    <span>Télécharger en PDF</span>
                    <DocumentArrowDownIcon className="ml-2 inline h-6 w-6" />
                  </button>
                  <button
                    className="flex w-full items-center justify-center rounded-xl border border-rgpd-blue px-4 py-2 text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-rgpd-white md:w-auto"
                    onClick={() => navigate(ROUTES.COMMUNICATION_UNSUBSCRIBERS)}
                  >
                    Gérer les désinscriptions
                  </button>
                </div>
              </div>
            </div>

            {/* Newsletter content layout */}
            <div className="mx-auto max-w-7xl px-4 pb-12">
              <div id="newsletter-content">
                <div className="relative">
                  {/* A4 Format Container */}
                  <div className="flex justify-center">
                    <div className="w-full" style={{ maxWidth: '210mm' }}>
                      <div 
                        className="origin-top bg-white shadow-md"
                        style={{
                          width: '210mm',
                          minHeight: '297mm',
                          transform: 'scale(var(--scale-factor, 1))',
                          transformOrigin: 'top left',
                          margin: '0 auto',
                        }}
                      >
                        <div
                          className="newsletter-content"
                          style={{
                            minHeight: '100%',
                            boxSizing: 'border-box',
                          }}
                          dangerouslySetInnerHTML={{ __html: processedContent }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal
        show={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        size="md"
        className="z-[1000]"
      >
        <Modal.Header>
          Confirmation d'export
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p><b>Voulez-vous télécharger la newsletter en PDF ?</b></p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex w-full justify-between">
            <button
              onClick={() => setIsConfirmModalOpen(false)}
              className="px-4 py-2 text-gray-500 hover:text-gray-700"
            >
              Non
            </button>
            <button
              onClick={handleDownloadPDF}
              className="rounded-xl bg-rgpd-blue px-4 py-2 text-white hover:bg-rgpd-blue-dark"
            >
              Oui
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default NewsletterPreview; 